import PinDropIcon from "@mui/icons-material/PinDrop";
import {
  Avatar,
  Box,
  Button,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { t } from "i18next";
import React from "react";
import { useNavigate } from "react-router-dom";
import { iresoftColors } from "../../../theme/ireColors";
import { AvatarData } from "../../../types/avatar";

interface LastPlacesScrollerProps {
  avatars: AvatarData[];
}

const LastPlacesScroller: React.FC<LastPlacesScrollerProps> = ({ avatars }) => {
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const maxAvatars: number = 10;

  const displayedAvatars = avatars.slice(0, maxAvatars);
  const avatarSize = isSmallScreen ? 56 : 72;

  const navigate = useNavigate();
  const handleClick = () => {
    navigate("/HistoryReading");
  };

  const repeatCount = isSmallScreen ? 10 : 1;
  const avatarsForScroll = new Array(repeatCount).fill(displayedAvatars).flat();

  return (
    <Box width="100%" p={2} sx={{ overflow: "hidden", pt: 3.5 }}>
      {avatarsForScroll.length > 0 && (
        <Tooltip
          title={t("pages.welcome.lastPlaces.tooltip.showMore")}
          placement="bottom"
        >
          <Box
            display="flex"
            alignItems="center"
            mb={3}
            justifyContent="center"
            sx={{ cursor: "pointer" }}
            onClick={handleClick}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={
                <PinDropIcon
                  sx={{
                    mr: 1,
                    color: iresoftColors.iresoftRed,
                    fontSize: isSmallScreen ? 20 : 24,
                  }}
                />
              }
              sx={{
                color: iresoftColors.iresoftRed,
                borderColor: iresoftColors.iresoftRed,
                borderRadius: 4,
                fontSize: isSmallScreen ? "0.875rem" : "1.05rem",
                fontWeight: 500,
                textTransform: "none",
                px: 2,
                py: 1,
                mb: 1,
              }}
            >
              {t("pages.welcome.lastPlaces.heading")}
            </Button>
          </Box>
        </Tooltip>
      )}

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "auto",
          cursor: isSmallScreen ? "grab" : "default",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            justifyContent: "center",
            minWidth: "100%",
          }}
        >
          {avatarsForScroll.map((a, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: "0 0 auto",
                mr: index === avatarsForScroll.length - 1 ? 0 : 3,
                px: isSmallScreen ? 0.5 : 1,
                mb: isSmallScreen ? 3 : 2.5,
              }}
            >
              <Avatar
                src={`data:image/jpeg;base64,${a.image}`}
                alt={`Avatar ${a.id}`}
                sx={{
                  width: avatarSize,
                  height: avatarSize,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                }}
              />
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                mt={0.7}
              >
                <PinDropIcon
                  sx={{ fontSize: 14, color: "gray", ml: -1, mb: 0.3, mr: 0.3 }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    maxWidth: isSmallScreen ? 80 : 100,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {a.placeName}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(LastPlacesScroller);
